// i18n
import {
  defaultCurrency,
  defaultLocale,
  failoverCurrency,
  supportCurrencies,
  translateInComponent,
} from 'infras/i18n';
import translations from 'presentations/views/components/pages/PricingPage/i18n.json';

import {
  Currency,
  IntervalType,
  PlanId,
} from 'domains/models/subscription/planId';
import { UserProfileItem, ViewerItem } from 'presentations/api/schema/user';
import { ReactNode, SyntheticEvent, useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
const stripePromise = loadStripe(String(process.env['STRIPE_API_KEY']));
import { useRouter } from 'next/router';
import { setBreadcrumbs } from 'presentations/views/helpers/breadcrumbs';
import {
  adminPaymentPath,
  apiPath,
  dashboardPath,
  pricingPath,
  signUpPath,
  termsPath,
} from 'presentations/routes/path';
import { get, patch, post } from 'infras/rest_client';
import { setEvent } from 'presentations/views/helpers/analytics';
import { ErrorList } from 'domains/models';
import { useChat } from 'react-live-chat-loader';
import { Role } from 'domains/models/team_user/role';
import {
  DowngradePlanBody,
  StripeSessionCreateBody,
  SubscriptionItem,
  SubscriptionStateItem,
} from 'presentations/api/schema/subscription';
import { getDateTime } from 'infras/datetime';
import Subscription from 'domains/models/subscription';
import { useRequest } from 'presentations/views/hooks/request';
import { latestPlanVersion } from 'domains/models/subscription/planVersion';
import CalendarIntegrationFeature from 'domains/models/subscription/features/integrations/calendar';
import { CancelReasonType } from 'domains/models/subscription/cancel_reason/cancel_reason_type';

export interface Props {
  viewer: ViewerItem | null;
  teamUid: string;
  cancelPlanId: PlanId | null;
  header: ReactNode;
}

export const usePricingPage = (props: Props) => {
  const t = translateInComponent(translations);
  const isLogin = props.viewer !== null;
  const [planId, setPlanId] = useState<PlanId | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<ErrorList>({});
  const [downgradeErrors, setDowngradeErrors] = useState<string[]>([]);
  const [downgradePlanId, setDowngradePlanId] = useState<number | null>(null);
  const [upgradePlanId, setUpgradePlanId] = useState<number | null>(null);
  const [selectPlanId, setSelectPlanId] = useState<PlanId | null>(null);
  const [
    subscriptionState,
    setSubscriptionState,
  ] = useState<SubscriptionStateItem | null>(null);
  const [currency, setCurrrency] = useState<Currency>(defaultCurrency);
  const [isCancelDowngradeModalOpen, setIsCancelDowngradeModalOpen] = useState<
    boolean
  >(false);
  const [isCancelDowngradeIsLoading, setIsCancelDowngradeIsLoading] = useState<
    boolean
  >(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
  const [state, loadChat] = useChat();

  useEffect(() => {
    if (props.viewer) {
      fetch();
    }
    setEvent('pricing page view', {
      isLogin: !!props.viewer,
      planId: props.viewer?.planId,
      planVersion: props.viewer?.planVersion,
      isFreeTrial:
        props.viewer &&
        Subscription.isDefaultFreeTrial(
          props.viewer.trialEndedAt,
          props.viewer.trialChannel,
        ),
    });
    if (props.cancelPlanId) {
      setEvent('cancel purchase', {
        planId: props.cancelPlanId,
      });
    }
  }, []);

  const subscriptionRequest = useRequest(
    props?.viewer?.team && props.viewer.role === Role.Owner
      ? apiPath(`/v1/admin/teams/${props.viewer.team.uid}/subscription`)
      : null,
  );
  const subscription = subscriptionRequest.data || null;

  const userProfileRequest = useRequest(
    props.viewer ? apiPath(`/v1/user/profile`) : null,
  );
  const userProfile: UserProfileItem | undefined = userProfileRequest.data;

  const router = useRouter();
  const intervalType: IntervalType = router.query.interval
    ? Number(router.query.interval)
    : IntervalType.Annually;
  const currentLocale = router.locale || defaultLocale;

  useEffect(() => {
    if (!router.locale) return;
    let c =
      router.locale.split('_')[0] === defaultLocale
        ? defaultCurrency
        : failoverCurrency;
    if (router.query.currency) {
      const queryCurrency = String(router.query.currency) as Currency;
      if (supportCurrencies.includes(queryCurrency)) {
        c = queryCurrency;
      }
    }
    setCurrrency(c);
  }, [router.isReady]);

  const fetch = async () => {
    setIsLoading(true);
    try {
      const results = await Promise.all([
        new Promise(async (resolve, reject) => {
          const result = await get(
            apiPath(`/v1/teams/${props.teamUid}/subscriptions`),
          );
          if (!result.isSuccess) {
            reject();
            return;
          }
          const subscription: SubscriptionItem = result.data;
          setPlanId(subscription.planId);
          resolve(true);
        }),
        new Promise(async (resolve, reject) => {
          if (!props.viewer) {
            resolve(null);
            return;
          }
          const result = await get(
            apiPath(`/v1/teams/${props.viewer.team!.uid}/subscriptions/state`),
          );
          if (!result.isSuccess) {
            reject();
            return;
          }
          setSubscriptionState(result.data);
          resolve(true);
        }),
      ]);
    } catch (error) {
      console.debug(error);
    }
    setIsLoading(false);
  };

  const onClickPrice = async (planId: PlanId) => {
    setSelectPlanId(planId);
    if (planId === PlanId.Enterprice) {
      loadChat({ open: true });
      return;
    }

    if (!isLogin) {
      window.location.href = signUpPath({
        to: pricingPath(),
        locale: currentLocale,
      });
      return;
    }

    if (!subscriptionState) {
      return;
    }

    if (
      subscriptionState.planId === PlanId.Free ||
      Subscription.isJicooTrialing(
        subscriptionState.trialChannel,
        getDateTime(subscriptionState.trialEndedAt),
      )
    ) {
      // フリープランから移行 or trial中の場合、checkoutにリダイレクト
      const body: StripeSessionCreateBody = {
        planId,
        currency,
        interval: intervalType,
        locale: currentLocale,
      };

      setIsLoading(true);
      const result = await post(
        apiPath(`/v1/teams/${props.teamUid}/subscriptions/session`, body),
      );
      if (!result.isSuccess) {
        setIsErrorModalOpen(true);
        if (Array.isArray(result.errors.calendarCount)) {
          const errorMessages = result.errors.calendarCount.filter(
            (error) => typeof error === 'string',
          );
          if (errorMessages.length > 0) {
            setDowngradeErrors(errorMessages);
          }
        }
        setIsLoading(false);
        return;
      }

      const sessionId = result.data;
      const stripeClient = await stripePromise;
      if (!stripeClient) return null;
      // When the customer clicks on the button, redirect them to Checkout.

      // https://stripe.com/docs/js/checkout/redirect_to_checkout
      //
      // Card for test
      // https://stripe.com/docs/testing
      const { error } = await stripeClient.redirectToCheckout({
        sessionId,
      });
      if (error) {
        console.log(error.message);
        setIsLoading(false);
        return;
      }
      router.push(dashboardPath());
    } else if (subscription.schedulePlanId) {
      setIsCancelDowngradeModalOpen(true);
    } else if (
      planId === PlanId.Free ||
      (planId < subscriptionState.planId &&
        subscription.currentIntervalType === intervalType)
    ) {
      // Freeプランへの移行 or (ダウングレード かつ 期間変更なし)
      setDowngradePlanId(planId);
      return;
    } else if (
      planId === PlanId.Pro &&
      subscription.currentIntervalType === IntervalType.Monthly &&
      intervalType === IntervalType.Annually
    ) {
      const errorList: string[] = [];
      const calendarIntegrationFeature = new CalendarIntegrationFeature({
        planVersion: subscription.planVersion,
      });
      if (
        subscriptionState.calendarCount >
        calendarIntegrationFeature.maxCount(planId)
      ) {
        errorList.push(
          t('Over calendar count', {
            max: calendarIntegrationFeature.maxCount(planId),
          }),
        );
      }
      if (subscriptionState.isAvailableProFeatureEventType) {
        errorList.push(t('Is Available Team Feature EventType'));
      }
      if (errorList.length > 0) {
        setDowngradeErrors(errorList);
        setIsErrorModalOpen(true);
      } else {
        setUpgradePlanId(planId);
      }
      return;
    } else {
      // それ以外はアップグレードダイアログを表示
      setUpgradePlanId(planId);
      return;
    }
  };

  const breadcrumbs = [];
  breadcrumbs.push({ title: t('title'), link: termsPath() });
  const routes = setBreadcrumbs(breadcrumbs);

  function onSubmit() {
    setErrors({});
  }

  function onCloseDowngradeModal() {
    setDowngradePlanId(null);
  }

  function onCancelUpgrade() {
    setUpgradePlanId(null);
  }

  function onClickPricingToggle() {
    const i =
      intervalType === IntervalType.Annually
        ? IntervalType.Monthly
        : IntervalType.Annually;
    router.push(pricingPath({ intervalType: i }));
  }

  function onClickChangeCurrency(e: SyntheticEvent<HTMLLinkElement>) {
    e.preventDefault();
    if (currency === failoverCurrency) return setCurrrency(defaultCurrency);
    setCurrrency(failoverCurrency);
  }

  async function onSubmitDowngrade(cancelReason: {
    reasons: {
      reason: CancelReasonType;
      detail?: string;
    }[];
    feedback: string;
  }) {
    if (!downgradePlanId) return;
    if (!subscriptionState) return;

    setIsLoading(true);
    const isFree = downgradePlanId === PlanId.Free;
    const data: DowngradePlanBody = {
      planId: downgradePlanId,
      cancelReason: isFree ? cancelReason : undefined,
    };
    const result = await patch(
      apiPath(`/v1/admin/teams/${props.teamUid}/subscription/downgrade_plan`),
      { data },
    );
    if (!result.isSuccess) {
      setErrors(result.errors);
      return;
    }

    subscriptionRequest.mutate({
      ...subscription,
      schedulePlanId: data.planId,
      schedulePlanVersion: latestPlanVersion,
    });

    setEvent('plan downgrade', {
      planId: data.planId,
      fromPlanId: props.viewer?.planId,
    });

    router.push(adminPaymentPath({ scheduleDowngarde: 1 }));
  }

  const onCancelDowngrade = async (e: any) => {
    if (!props?.viewer?.team) {
      return;
    }

    setIsCancelDowngradeIsLoading(true);
    const result = await patch(
      apiPath(
        `/v1/admin/teams/${props.viewer.team.uid}/subscription/cancel_downgrade`,
      ),
      {},
    );
    if (!result.isSuccess) {
      return;
    }

    subscriptionRequest.mutate({
      ...subscription,
      schedulePlanId: null,
      schedulePlanVersion: null,
    });
    router.push(adminPaymentPath({ cancelDowngarde: 1 }));
  };

  const onCloseCancelDowngradeModal = (e: any) => {
    setIsCancelDowngradeModalOpen(false);
  };

  const onCloseErrorModal = (e: any) => {
    setIsErrorModalOpen(false);
    setDowngradeErrors([]);
  };

  let canChange = true;
  if (props.viewer) {
    canChange = props.viewer.role === Role.Owner;
  }

  return {
    state: {
      isLogin,
      canChange,
      planId,
      planVersion: subscriptionState ? subscriptionState.planVersion : null,
      isLoading,
      trialStartedAt: subscriptionState?.trialStartedAt,
      trialEndedAt: subscriptionState?.trialEndedAt,
      trialChannel: subscriptionState?.trialChannel,
      currency,
      isErrorModalOpen,
      selectPlanId,
      downgradeErrors,
      upgradePlanId,
      downgradePlanId,
      schedulePlanId: subscription?.schedulePlanId,
      schedulePlanVersion: subscription?.schedulePlanVersion,
      intervalType,
      currentPeriodEndedAt: subscription?.currentPeriodEndedAt
        ? getDateTime(subscription.currentPeriodEndedAt)
        : null,
      currentIntervalType: subscription?.currentIntervalType,
      userProfile,
      isCancelDowngradeModalOpen,
      isCancelDowngradeIsLoading,
    },
    actions: {
      onClickPrice,
      onClickChangeCurrency,
      onSubmit,
      onSubmitDowngrade,
      onCloseDowngradeModal,
      onCancelUpgrade,
      onClickPricingToggle,
      onCloseCancelDowngradeModal,
      onCancelDowngrade,
      onCloseErrorModal,
    },
  };
};
